import { Box, FormControl, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectCase, selectCaseClaimantName } from "redux/slices/case/";
import { selectClientLensAssignees, selectClientLensCaseHomeGroups } from "redux/slices/clientLens";
import { selectCollectEnabled, selectEnableFirmFeatureSet } from "redux/slices/session";
import CopyToClipboard from "components/CopyToClipboard";
import { createPortal } from "react-dom";
import { AppRoutes } from "AppRoutes";
import CaseAutocomplete from "components/CaseAutocomplete";
import { PortalContext } from "contexts/PortalContext";
import { useContext, useState } from "react";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { PCopy } from "components/PCopy";
import People from "@mui/icons-material/People";
import FirmPickerDialog from "components/FirmPickerDialog";
import { useGroupLocations } from "routes/lens-editor/custom-properties/useGroupLocations";
import SBRCaseInfoBox from "./CaseInfo";
import CaseStatus from "routes/lenses/lens/case/Status";
import SBRCaseIntakesOverview from "./Intakes";
import SBRCaseExports from "./Exports";
import SBRCaseActions from "./Actions";
import { SBRCaseDisplayGroups } from "./CaseDisplayGroups";

export default function SBRCaseHome() {
  const navigate = useNavigate();
  const { lensId, caseId } = useParamNumbers(["lensId", "caseId"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const nameVariant = isMobile ? "h5" : "h4";
  const lensCase = useAppSelector(selectCase);
  const caseInfo = lensCase.info;
  const clientLensAssignees = useAppSelector(selectClientLensAssignees);
  const showFirm = useAppSelector(selectEnableFirmFeatureSet);
  const claimantName = useAppSelector(selectCaseClaimantName);
  const caseHomeDisplayGroups = useAppSelector(selectClientLensCaseHomeGroups);
  const groupLocations = useGroupLocations(caseHomeDisplayGroups);
  const [showFirmPicker, setShowFirmPicker] = useState(false);
  const { portalRefElement } = useContext(PortalContext);
  const collectEnabled = useAppSelector(selectCollectEnabled);

  if (lensId === undefined || caseId === undefined) {
    navigate("../");
    return null;
  }

  const navigateToCase = (caseId: number) => {
    navigate(AppRoutes.caseDetailBuilder(lensId, caseId));
  };

  return (
    <>
      {portalRefElement &&
        createPortal(
          <Box sx={{ display: "flex" }}>
            {!isMobile && (
              <FormControl sx={{ width: "300px", ml: 2 }}>
                <CaseAutocomplete
                  label="Search for a case"
                  selected={undefined}
                  onChange={(selectedCase) => {
                    if (selectedCase) {
                      navigateToCase(selectedCase.caseId);
                    }
                  }}
                  lensId={lensId}
                />
              </FormControl>
            )}
          </Box>,
          portalRefElement
        )}
      <div className="flex-1 min-h-0 h-full flex flex-col gap-4 w-full min-w-0 pt-4">
        <div className="flex items-center gap-4">
          <PCopy text={claimantName} pProps={{ variant: nameVariant }}>
            {claimantName}
          </PCopy>
          <div className="flex flex-col">
            {showFirm && lensCase.plaintiffFirm?.name && (
              <p className="font-bold text-lg text-gray-300">{lensCase.plaintiffFirm.name}</p>
            )}
            <div className="flex items-center">
              <div className="flex items-end">
                <p className="font-bold text-gray-300">{caseInfo.clientCaseId}</p>
                <CopyToClipboard text={caseInfo.clientCaseId} />
              </div>
              {collectEnabled && (
                <div>
                  <IconButton
                    onClick={() => {
                      setShowFirmPicker(true);
                    }}>
                    <People />
                  </IconButton>
                  {showFirmPicker && <FirmPickerDialog open={showFirmPicker} setOpen={setShowFirmPicker} />}
                </div>
              )}
            </div>
          </div>
          <CaseStatus caseId={lensCase.id} status={lensCase.clientStatus} />
        </div>
        <div className="grid grid-cols-[minmax(0,1fr)_420px] gap-4 pb-4">
          <div className="flex flex-col gap-4">
            <SBRCaseInfoBox ccase={lensCase} assignees={clientLensAssignees} initialEditMode={false} />
            <SBRCaseDisplayGroups groups={groupLocations.caseHome.map((gl) => gl.group)} />
            <SBRCaseIntakesOverview lensId={lensId} caseId={caseId} lensCase={lensCase} />
          </div>
          <div className="flex flex-col gap-4">
            <SBRCaseActions lensCase={lensCase} />
            <SBRCaseExports caseId={caseId} />
          </div>
        </div>
      </div>
    </>
  );
}
